import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {combineLatest, debounceTime, distinctUntilChanged, filter, map, Observable, startWith, Subject, takeUntil, tap} from 'rxjs';
import {PortalStateService} from 'src/app/core/services/portal-state.service';
import Icons from 'src/app/core/enums/icons.enum';
import {PortalApp} from 'src/app/core/shared/portal-app';
import {AppGroup, Application} from 'src/portaljs/constants/application';
import {AuthService} from 'src/app/core/services/auth.service';
import {ToastMessageService} from 'src/app/core/services/toast-message.service';
import {SiteModel} from 'src/app/core/models/sites.model';
import {UserService} from 'src/app/core/services/user.service';
import * as _ from 'lodash-es';
import {AuthenticationEvent} from 'src/app/core/models/authentication-event.model';
import LoginPrompt from 'src/app/core/enums/login-prompt.enum';
import AuthType from 'src/app/core/enums/auth.type.enum';
import {ToolbarService} from 'src/app/core/services/toolbar.service';
import {UserLoginModel} from 'src/app/core/models/user-login.model';
import {ClipboardService} from 'ngx-clipboard';
import {EnvironmentService} from '../../../core/services/environment.service';
import {DialogModeType} from '../../../core/shared/error/dialog-message-type.enum';
import { UnifiedSettingsService } from 'src/app/core/services/unified-settings.service';
import { AppAction } from 'src/portaljs/models/app-action';

@Component({
    selector: 'fss-portal-district-sites',
    templateUrl: './district-sites.component.html',
    styleUrls: ['./district-sites.component.scss'],
    standalone: false
})
export class DistrictSitesComponent implements OnDestroy, OnInit {
  private ngUnsubscribe = new Subject();
  public icons = Icons;
  public filterText: string = '';
  public user: UserLoginModel;
  protected readonly Application = Application;
  public homePage: string;
  public collapsedAccordions: string[] = [];
  public isCUSTOM_HOMEPAGE = this.environmentService.isCUSTOM_HOMEPAGE();

  @ViewChild('schoolFilterInput') schoolFilterInput: ElementRef;
  public siteMap: any = {};
  public siteTypeOrder: any = {};

  public goToSettings() {
    const originalApp: PortalApp = this.portalStateService.getActiveApp();
    const settingsApp: PortalApp = this.portalStateService.getSelectableApp(Application.UNIFIED_SETTINGS);
    if (settingsApp) {
      const appAction: AppAction = {destination: Application.UNIFIED_SETTINGS, source: originalApp.app, route: 'Settings'};
      this.portalStateService.setActiveApp(settingsApp, appAction);
    }
  }

  public appsByGroup: any;
  public selectableApps$: Observable<PortalApp[]> = this.portalStateService.getSelectableApps$()
    .pipe(
      tap(rez => {
        this.appsByGroup = _.groupBy(rez, (data: PortalApp) => {
          if([Application.FOLLETT_COMMUNITY].indexOf(data.app) >= 0 )
            return AppGroup.SUPPORT;
          else if([Application.DESTINY_EXPRESS, Application.TITLEWAVE].indexOf(data.app) >= 0 )
            return AppGroup.SHOP;
          return AppGroup.DESTINY;
        });
      })
  );

  /* Filter for user site filter text */
  private _filterSchoolsInput = new Subject<string>();
  public filterSchoolsInput$: Observable<string> = this._filterSchoolsInput.asObservable()
    .pipe(
      startWith(''),
      debounceTime(100),
      distinctUntilChanged()
    );
  public get AppGroup() {
    return [AppGroup.DESTINY, AppGroup.SHOP, AppGroup.SUPPORT];
  }

  /* List of the user's enrolled sites */
  public enrolledSites$: Observable<SiteModel[]> = this.userService.getEnrolledSitesInDistrict$();

  /* Filtered list of district sites based on the user's search term */
  public filteredSites$: Observable<SiteModel[]> = combineLatest([this.enrolledSites$, this.filterSchoolsInput$])
    .pipe(
      map((rez: [SiteModel[], string]) => {
        this.filterText = rez[1];
        const fsites: SiteModel[] = _.filter(rez[0], (site) => {
          return (typeof this.filterText === 'string') && site.siteName.toLowerCase().includes(this.filterText.toLowerCase());
        });

        this.siteMap = {};
        let existingsites: SiteModel[] = [];
        for (const fs of fsites) {
          const siteMapName = fs.siteType && fs.siteType.name ? fs.siteType.name : 'Other Collections';
          existingsites = this.siteMap[siteMapName] || [];
          existingsites.push(fs);
          this.siteMap[siteMapName] = existingsites;
          this.siteTypeOrder[siteMapName] = fs.siteType?.priorityOrder || 99;
        }

        return fsites;
      })
    );

  constructor(private portalStateService: PortalStateService, private authService: AuthService,
        private toastService: ToastMessageService, private userService: UserService,
        private toolbarService: ToolbarService, private clipboardService: ClipboardService,
        private environmentService: EnvironmentService, private settingsService: UnifiedSettingsService) {
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public ngOnInit(): void {
    this.selectableApps$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();


    this.settingsService.getUnifiedDistrictSettings$()
      .pipe(
        filter(settings => !!settings),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((settings) => {
        this.homePage = settings.getHomePageData();
      });

    this.filterSchoolsInput$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();

    this.authService.getUserLogin$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((ulm: UserLoginModel) => {
        this.user = ulm;
    });

    setTimeout(()=>this.portalStateService.setShowLoadingSpinner(false));
  }


  public getSiteTypes() {
    return Object.keys(this.siteMap).sort((a, b) => this.siteTypeOrder[a] - this.siteTypeOrder[b] );
  }

  public getSitesByTypes(siteType: string) {
    return this.siteMap[siteType];
  }

  public switchSiteTo(site: SiteModel) {
    this.toolbarService.isMobileMenuOpen = false;
    if (this.user) {
      this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.SITE_SWITCH, site.guid, 'switch-siteforuser'));
    } else {
      this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.LOGIN, site.guid, 'switch-site-nouser'));
    }
  }

  public filterSchools(event: any) {
    this._filterSchoolsInput.next(event.target.value);
  }

  public resetSearchFilter() {
    this.filterText = '';
    this._filterSchoolsInput.next('');
  }

  public focus(): void {
    if (this.schoolFilterInput) {
      this.schoolFilterInput.nativeElement?.focus();
    }
  }

  public addCollapsed(id: string) {
    if (this.collapsedAccordions.includes(id)) {
      this.collapsedAccordions = this.collapsedAccordions.filter(expandedIds => expandedIds !== id);
    } else {
      this.collapsedAccordions.push(id);
    }
  }

  public isCollapsed(id: string) {
    return this.collapsedAccordions.includes(id);
  }

  public isDestinyAdmin() {
    return this.authService.isDestinyAdmin();
  }

  public handleInputKeyEvent(event: KeyboardEvent) {
    // Only let 'Escape' events propagate so we can keep focus in this component
    if (event.code !== 'Escape') {
      event.stopPropagation();
    }
  }

  copyLink(st: SiteModel, app: Application) {
    const theUrl = this.environmentService.getUrl(Application.PORTAL) + '/portal?appId=' + this.authService.getDistrictAppId() + '&siteGuid=' + st.guid + '&app=' + encodeURIComponent(app);
    this.clipboardService.copyFromContent(theUrl);
    this.toastService.show(DialogModeType.INFO, 'Link copied to clipboard', false);
  }

  public getHomePage() {
    return this.homePage;
  }

  public getSiteNameWithId(site: SiteModel): string {
    if (site?.siteId === undefined || site?.siteId === null) {
      return site.siteName;
    } else {
      return site.siteName + ' (' + site.siteId + ')';
    }
  }
  
}
